import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import PostMioLogo from '../components/PostMioLogo'

// ── Palette ────────────────────────────────────────────────
const G = {
  bg:      '#0A0A0A',
  base:    '#111111',
  card:    '#141414',
  border:  '#2C2C2C',
  gold:    '#C9922A',
  goldLt:  '#E5B050',
  goldGlow:'rgba(201,146,42,0.12)',
  smoke:   '#7A7A7A',
  cream:   '#F0EDE8',
  textSec: '#A0A0A0',
  textMut: '#606060',
}

// ── i18n ───────────────────────────────────────────────────
const UI_TEXT = {
  IT: {
    nav: { about: 'Chi siamo', home: 'Home', signup: 'Registrati' },
    pageTitle: 'Cosa dicono di noi',
    empty: 'Nessuna recensione ancora. Torna presto!',
    cta: 'Prova PostMio™ gratis per 7 giorni',
    footer: {
      tagline: 'Fatto con ❤️ in Italia · Da un piccolo imprenditore - per i piccoli imprenditori',
      about: 'Chi siamo',
      login: 'Accedi',
      signup: 'Registrati',
      legal: `© ${new Date().getFullYear()} PostMio™ · P.IVA in corso di registrazione`,
    },
  },
  EN: {
    nav: { about: 'About', home: 'Home', signup: 'Sign up' },
    pageTitle: 'What people say',
    empty: 'No reviews yet. Check back soon!',
    cta: 'Try PostMio™ free for 7 days',
    footer: {
      tagline: 'Made with ❤️ in Italy · By a small business owner - for small business owners',
      about: 'About',
      login: 'Log in',
      signup: 'Sign up',
      legal: `© ${new Date().getFullYear()} PostMio™`,
    },
  },
  ES: {
    nav: { about: 'Acerca de', home: 'Inicio', signup: 'Registrarse' },
    pageTitle: 'Lo que dicen las personas',
    empty: 'Aún no hay reseñas. ¡Vuelve pronto!',
    cta: 'Prueba PostMio™ gratis durante 7 días',
    footer: {
      tagline: 'Hecho con ❤️ en Italia · Por un pequeño empresario - para pequeños empresarios',
      about: 'Acerca de',
      login: 'Iniciar sesión',
      signup: 'Registrarse',
      legal: `© ${new Date().getFullYear()} PostMio™`,
    },
  },
  DE: {
    nav: { about: 'Über uns', home: 'Startseite', signup: 'Registrieren' },
    pageTitle: 'Was die Leute sagen',
    empty: 'Noch keine Bewertungen. Schauen Sie bald wieder vorbei!',
    cta: 'Testen Sie PostMio™ 7 Tage lang kostenlos',
    footer: {
      tagline: 'Mit ❤️ in Italien gemacht · Von einem Kleinunternehmer - für Kleinunternehmer',
      about: 'Über uns',
      login: 'Anmelden',
      signup: 'Registrieren',
      legal: `© ${new Date().getFullYear()} PostMio™`,
    },
  },
  FR: {
    nav: { about: 'À propos', home: 'Accueil', signup: 'S\'inscrire' },
    pageTitle: 'Ce que les gens disent',
    empty: 'Pas encore d\'avis. Revenez bientôt !',
    cta: 'Essayez PostMio™ gratuitement pendant 7 jours',
    footer: {
      tagline: 'Fait avec ❤️ en Italie · Par un petit entrepreneur - pour les petits entrepreneurs',
      about: 'À propos',
      login: 'Se connecter',
      signup: 'S\'inscrire',
      legal: `© ${new Date().getFullYear()} PostMio™`,
    },
  },
  PL: {
    nav: { about: 'O nas', home: 'Strona główna', signup: 'Zarejestruj się' },
    pageTitle: 'Co mówią o nas',
    empty: 'Brak opinii. Wróć wkrótce!',
    cta: 'Wypróbuj PostMio™ za darmo przez 7 dni',
    footer: {
      tagline: 'Zrobione z ❤️ we Włoszech · Od małego przedsiębiorcy - dla małych przedsiębiorców',
      about: 'O nas',
      login: 'Zaloguj się',
      signup: 'Zarejestruj się',
      legal: `© ${new Date().getFullYear()} PostMio™`,
    },
  },
}

function detectLang() {
  const lang = (navigator.language || '').toLowerCase()
  if (lang.startsWith('pl')) return 'PL'
  if (lang.startsWith('en')) return 'EN'
  if (lang.startsWith('es')) return 'ES'
  if (lang.startsWith('de')) return 'DE'
  if (lang.startsWith('fr')) return 'FR'
  return 'IT'
}

function renderTM(s) {
  if (typeof s !== 'string' || !s.includes('™')) return s
  const i = s.indexOf('™')
  return <>{s.slice(0, i)}<sup style={{fontSize:'0.5em',verticalAlign:'super',lineHeight:0}}>™</sup>{s.slice(i + 1)}</>
}

function StarDisplay({ rating }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(star => (
        <span key={star} style={{ color: rating >= star ? G.gold : G.smoke, fontSize: 18 }}>★</span>
      ))}
    </div>
  )
}

function formatMonthYear(dateStr) {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

export default function Reviews() {
  const t = UI_TEXT[detectLang()]
  const [displayReviews, setDisplayReviews] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchReviews() {
      const { data } = await supabase
        .from('reviews')
        .select('*')
        .eq('approved', true)
        .order('created_at', { ascending: false })
      const lang = detectLang()
      if (!data || data.length === 0) {
        setDisplayReviews(data || [])
        setLoading(false)
        return
      }
      try {
        const res = await fetch('/api/translate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ texts: data.map(r => r.review_text), targetLang: lang })
        })
        if (!res.ok) throw new Error(`translate ${res.status}`)
        const json = await res.json()
        const translated = json.translated || data.map(r => r.review_text)
        setDisplayReviews(data.map((r, i) => ({ ...r, review_text: translated[i] ?? r.review_text })))
      } catch {
        setDisplayReviews(data)
      }
      setLoading(false)
    }
    fetchReviews()
  }, [])

  return (
    <div style={{ background: G.bg, fontFamily: "'DM Sans', sans-serif", color: G.cream, minHeight: '100vh' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:wght@600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${G.bg}; }
        .reviews-cta-primary {
          background: ${G.gold}; color: #0A0A0A;
          padding: 15px 36px; border-radius: 10px;
          font-size: 16px; font-weight: 700; border: none; cursor: pointer;
          font-family: 'DM Sans', sans-serif; letter-spacing: 0.2px;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          display: inline-flex; align-items: center; gap: 8px;
          text-decoration: none;
        }
        .reviews-cta-primary:hover {
          background: ${G.goldLt};
          color: #0A0A0A;
          transform: translateY(-1px);
          box-shadow: 0 6px 24px rgba(201,146,42,0.3);
        }
      `}</style>

      {/* ── NAVBAR ─────────────────────────────────────────── */}
      <nav className="site-nav" style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(10,10,10,0.92)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${G.border}`,
        padding: '0 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: 60,
      }}>
        <Link to="/" style={{ textDecoration: 'none', lineHeight: 0 }}>
          <PostMioLogo size="md" variant="dark" />
        </Link>
        <div className="site-nav-links" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link to="/" style={{ fontSize: 14, color: G.smoke, textDecoration: 'none', fontWeight: 500, transition: 'color 0.2s' }}
            onMouseEnter={e => e.target.style.color = G.cream}
            onMouseLeave={e => e.target.style.color = G.smoke}
          >
            {t.nav.home}
          </Link>
          <Link to="/signup" style={{
            background: G.gold, color: '#0A0A0A',
            padding: '8px 18px', borderRadius: 10,
            fontSize: 13, fontWeight: 700,
            fontFamily: "'DM Sans', sans-serif",
            letterSpacing: '0.2px',
            textDecoration: 'none',
            transition: 'background 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.background = G.goldLt}
            onMouseLeave={e => e.currentTarget.style.background = G.gold}
          >
            {t.nav.signup}
          </Link>
        </div>
      </nav>

      {/* ── MAIN CONTENT ───────────────────────────────────── */}
      <main style={{ maxWidth: 960, margin: '0 auto', padding: '72px 24px 80px' }}>

        <h1 style={{
          fontFamily: "'Lora', serif",
          fontSize: 'clamp(28px, 5vw, 48px)',
          fontWeight: 700,
          color: G.cream,
          textAlign: 'center',
          marginBottom: 56,
          lineHeight: 1.2,
        }}>
          {t.pageTitle}
        </h1>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
            <div style={{
              width: 40, height: 40,
              border: `2px solid ${G.border}`,
              borderTop: `2px solid ${G.gold}`,
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : displayReviews.length === 0 ? (
          <p style={{ textAlign: 'center', color: G.smoke, fontSize: 18, paddingTop: 24 }}>
            {t.empty}
          </p>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 24,
          }}>
            {displayReviews.map((review) => (
              <div key={review.id} style={{
                background: G.card,
                border: `1px solid ${G.border}`,
                borderRadius: 8,
                padding: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}>
                <StarDisplay rating={review.rating} />
                <p style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 15,
                  color: G.cream,
                  lineHeight: 1.6,
                  flex: 1,
                }}>
                  {review.review_text}
                </p>
                <div>
                  <div style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: 14,
                    color: G.gold,
                  }}>
                    {review.name}
                  </div>
                  {review.business_type && (
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 13,
                      color: G.textSec,
                      marginTop: 2,
                    }}>
                      {review.business_type}
                    </div>
                  )}
                  <div style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 12,
                    color: G.smoke,
                    marginTop: 4,
                  }}>
                    {formatMonthYear(review.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── CTA ──────────────────────────────────────────── */}
        <div style={{ textAlign: 'center', marginTop: 64 }}>
          <span className="reviews-cta-primary" style={{ cursor: 'default', opacity: 0.6, pointerEvents: 'none' }}>
            🚀 Coming Soon — Stay tuned for launch!
          </span>
        </div>

      </main>

      {/* ── FOOTER ─────────────────────────────────────────── */}
      <footer style={{ background: G.bg, borderTop: `1px solid ${G.border}`, padding: '40px 24px', textAlign: 'center' }}>
        <PostMioLogo size="md" variant="dark" />
        <p style={{ fontSize: 14, color: G.textMut, marginTop: 16, lineHeight: 1.5 }}>
          {t.footer.tagline}<br />
          <Link to="/about" style={{ color: G.textMut, textDecoration: 'none' }}>{t.footer.about}</Link>
          {' · '}
          <Link to="/login" style={{ color: G.textMut, textDecoration: 'none' }}>{t.footer.login}</Link>
          {' · '}
          <Link to="/signup" style={{ color: G.textMut, textDecoration: 'none' }}>{t.footer.signup}</Link>
          {' · '}
          <Link to="/privacy" style={{ color: G.textMut, textDecoration: 'none' }}>Privacy Policy</Link>
        </p>
        <p style={{ fontSize: 13, color: G.border, marginTop: 12, lineHeight: 1.5 }}>
          {renderTM(t.footer.legal)}
        </p>
      </footer>
    </div>
  )
}
